import './link-card.scss';

import type { PropsWithChildren, ReactNode } from 'react';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';

import { Link } from '../../atoms/link/link.tsx';
import { cn } from '../../utils/helpers.ts';
import {
  ItemCard,
  ItemCardBody,
  ItemCardMeta,
  ItemCardSection,
} from '../item-card/item-card.tsx';

export type LinkCardProps = PropsWithChildren<{
  href: string;
  onClick?: () => void;
  className?: string;
  graphic?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  hideChevron?: boolean;
  staticPadding?: boolean;
}>;

export const LinkCard: React.FC<LinkCardProps> = ({
  href,
  onClick,
  className,
  graphic,
  title,
  subtitle,
  hideChevron = false,
  staticPadding,
  children,
}) => {
  const content =
    children ??
    ((title || subtitle) && (
      <>
        {title && (
          <div className="LinkCard__title label-potent label-md md:label-lg md:label-potent">
            {title}
          </div>
        )}
        {subtitle && (
          <div className="LinkCard__subtitle label-moderate label-md md:label-lg md:label-moderate">
            {subtitle}
          </div>
        )}
      </>
    ));

  return (
    <ItemCard
      asChild
      className={cn(
        'LinkCard block cursor-pointer no-underline w-full relative flex items-center',
        className,
      )}
    >
      <Link href={href} onClick={onClick}>
        <ItemCardSection staticPadding={staticPadding}>
          <ItemCardBody
            className={cn('LinkCard__body', {
              'LinkCard__body--no-graphic': !graphic,
              'LinkCard__body--no-chevron': hideChevron,
            })}
          >
            {graphic && (
              <div className="flex shrink-0 items-center justify-center">
                {graphic}
              </div>
            )}

            <div className="LinkCard__content flex flex-1 flex-col gap-snug-md">
              {content}
            </div>

            {!hideChevron && (
              <ItemCardMeta>
                <div className="LinkCard__chevron flex shrink-0 items-center">
                  <ArkIcon iconName="chevron-right" size="1.25rem" />
                </div>
              </ItemCardMeta>
            )}
          </ItemCardBody>
        </ItemCardSection>
      </Link>
    </ItemCard>
  );
};

export const LinkCardIcon: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="LinkCard__icon p-comfy-xs rounded-md">{children}</div>
);
